import { useAnalytics } from "@/modules/analytics/analytics.ts";
import { isAdminPath } from "@/modules/main/adminContext.ts";
import { getCurrentRouteId } from "@/utils/router/getCurrentRouteId.ts";
import type { RegisteredRouter } from "@tanstack/react-router";
import { useEffect } from "react";

/**
 * Remove `$param` placeholders from a route path to make it easier to match (and not dependent on internal naming
 * details).
 *
 * "/compute/vm/$location/$vmId/" -> "/compute/vm/__/__/"
 */
function normalizeRoutePath(path: string) {
    return path.replace(/\$\w+/g, "__");
}

function getNormalizedPath(router: RegisteredRouter) {
    const fullPath = getCurrentRouteId(router.state);
    return normalizeRoutePath(fullPath);
}

/** Send a page view event on each page load of the {@link router} */
export function useRouterAnalytics(router: RegisteredRouter) {
    const analytics = useAnalytics();

    // console.debug("initial state: %o", router.state);

    useEffect(() => {
        function sendPageViewEvent() {
            const path = getNormalizedPath(router);
            // Filter admin paths, for now (should we track these?)
            if (isAdminPath(path)) {
                return;
            }

            // TODO: Add referrer
            analytics.event({ event: "page_view", page_location: path });
        }

        // Send initial page view event
        sendPageViewEvent();

        // Send events on each page load (note that the first load does not currently seem to trigger "onLoad")
        return router.subscribe("onLoad", (_location) => {
            sendPageViewEvent();
        });
    }, [analytics, router]);
}
